import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eraser, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lots the Portfolio rows never show (fractional rounding dust or lots in a
// portfolio the user no longer has) but which still drove "$0.00 received"
// bell alerts. Lists them with a one-click Clear.
export default function DustLotsBanner({ onCleared }) {
  const [lots, setLots] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/portfolio/dust`, { withCredentials: true })
      .then((r) => setLots(r.data?.lots || []))
      .catch(() => setLots([]));
  }, []);

  useEffect(() => {
    load();
    window.addEventListener('portfolio-changed', load);
    return () => window.removeEventListener('portfolio-changed', load);
  }, [load]);

  if (lots.length === 0) return null;

  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/portfolio/dust/sweep`, { position_ids: lots.map((l) => l.position_id) }, { withCredentials: true });
      toast.success(`Cleared ${data?.deleted ?? lots.length} leftover lot${(data?.deleted ?? lots.length) === 1 ? '' : 's'}`);
      setLots([]);
      onCleared?.();
      window.dispatchEvent(new Event('portfolio-changed'));
    } catch {
      toast.error('Could not clear leftover lots');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs flex flex-wrap items-center gap-2" data-testid="dust-lots-banner">
      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
      <span className="font-semibold">Leftover lots not shown in your rows:</span>
      <span className="flex flex-wrap gap-1.5">
        {lots.map((l) => (
          <span key={l.position_id} className="font-mono px-1.5 py-0.5 rounded bg-background/60 border border-border" data-testid={`dust-lot-${l.ticker}`}>
            {l.ticker} {Number(l.shares).toFixed(4)} sh{l.reason === 'orphan_portfolio' ? ' · old portfolio' : ''}
          </span>
        ))}
      </span>
      <span className="text-muted-foreground">These are rounding residue from sells/DRIP or lots in a removed portfolio. They still trigger payment alerts until cleared.</span>
      <button
        type="button"
        onClick={clear}
        disabled={busy}
        className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-600 hover:bg-amber-500 text-white font-semibold disabled:opacity-50"
        data-testid="dust-lots-clear"
      >
        <Eraser className="w-3.5 h-3.5" /> Clear {lots.length}
      </button>
    </div>
  );
}
